import { useMemo, useCallback, createElement } from 'react';
import { Shipment } from 'src/generated/services/TFinancialApi';
import { IShipmentOption, OptionType } from './types';
import { groupNrcfItemsFirst } from './utils';
import { ShipmentSectionHeader } from './ShipmentSectionHeader';
import { ShipmentOptionItem } from './ShipmentOptionItem';
import { ShipmentSelectedValue } from './ShipmentSelectedValue';
import { useInvoiceContext } from '../../InvoiceContextProvider';

const getReviewText = (workflow?: string, sendFlaggedToRole?: string): string | undefined => {
  if (workflow === 'PROCUREMENT_REVIEW') return 'Procurement Review';
  if (workflow === 'PROCUREMENT_NO_CONTRACT_REVIEW') return sendFlaggedToRole === 'SUPPLY_CHAIN' ? 'Supply Chain Review' : 'Procurement Review';
  if (workflow === 'SUPPLY_CHAIN_REVIEW') return 'Supply Chain Review';
  if (workflow === 'FINANCE_REVIEW') return 'Finance Review';
  if (workflow === 'OPERATION_REVIEW') return 'Logistics Review';
  if (workflow === 'OPS_ADMIN_REVIEW') return 'Ops Admin Review';
  if (workflow === 'OPS_ADMIN_NO_CONTRACT_REVIEW') return 'Ops Admin Review';
  return undefined;
};

export const useShipmentOptions = () => {
  const { invoiceSummary } = useInvoiceContext();

  const resolveReviewText = useCallback(
    (option: IShipmentOption) => getReviewText(option.currentDeviationWorkflow, (invoiceSummary as any)?.sendFlaggedToRole),
    [invoiceSummary]
  );

  // Create section header element
  const createSectionHeader = useCallback(
    (option: IShipmentOption) => {
      const reviewText = option.type === 'ACTION_NEEDED' ? resolveReviewText(option) : undefined;
      return createElement(ShipmentSectionHeader, { option, reviewText });
    },
    [resolveReviewText]
  );

  // Create shipment option element with optional highlighting
  const createShipmentOption = useCallback((option: IShipmentOption, searchTerm: string = '') => createElement(ShipmentOptionItem, { option, searchTerm }), []);

  // Create selected value element
  const createSelectedValue = useCallback(
    (option: IShipmentOption) => {
      const reviewText = option.type === 'ACTION_NEEDED' || option.type === 'PENDING' ? resolveReviewText(option) : undefined;
      return createElement(ShipmentSelectedValue, { option, reviewText });
    },
    [resolveReviewText]
  );

  const getOptionLabel = useCallback(
    (name: string, title: string, itemsCount?: number, itemsTotal?: number): IShipmentOption => {
      const option: IShipmentOption = {
        isLabel: true,
        value: name,
        type: name as OptionType,
        title,
        itemsCount,
        itemsTotal,
        label: null as any,
        valueRender: null as any,
      };

      option.label = createSectionHeader(option);
      return option;
    },
    [createSectionHeader]
  );

  const getOptionData = useCallback(
    (data: Shipment, type: string): IShipmentOption => {
      const isLocalActionNoRateCard =
        !invoiceSummary?.nextWorkflowAction &&
        data?.status === 'FLAGGED' &&
        (data?.currentDeviationWorkflow === 'PROCUREMENT_NO_CONTRACT_REVIEW' || data?.currentDeviationWorkflow === 'OPS_ADMIN_NO_CONTRACT_REVIEW');

      const option: IShipmentOption = {
        value: data.shipmentInvoiceId,
        shipmentNumber: data.shipmentNumber,
        type: type as OptionType,
        status: data.status,
        deviationCount: data.numberOfDeviations,
        approvalCount: data.numberOfApprovals,
        isNoRateCard: isLocalActionNoRateCard,
        currentDeviationWorkflow: data.currentDeviationWorkflow,
        auditStatus: data.auditStatus,
        label: null as any,
        valueRender: null as any,
      };

      option.label = createShipmentOption(option);
      option.valueRender = createSelectedValue(option);

      return option;
    },
    [invoiceSummary?.nextWorkflowAction, createShipmentOption, createSelectedValue]
  );

  const options = useMemo(() => {
    const items = invoiceSummary?.shipmentList;
    const servicesListOptions: IShipmentOption[] = [];

    const actionNeededItems = items?.ACTION_NEEDED && items.ACTION_NEEDED.length > 0 ? groupNrcfItemsFirst(items.ACTION_NEEDED) : [];
    const pendingItems = items?.PENDING && items.PENDING.length > 0 ? groupNrcfItemsFirst(items.PENDING) : [];
    const resolvedItems = items?.RESOLVED && items.RESOLVED.length > 0 ? groupNrcfItemsFirst(items.RESOLVED) : [];
    const processingItems = items?.PROCESSING && items.PROCESSING.length > 0 ? groupNrcfItemsFirst(items.PROCESSING) : [];
    const underReviewItems = items?.UNDER_REVIEW && items.UNDER_REVIEW.length > 0 ? groupNrcfItemsFirst(items.UNDER_REVIEW) : [];
    const totalItems = actionNeededItems.length + pendingItems.length + resolvedItems.length + processingItems.length + underReviewItems.length;

    // If only resolved items exist, show as "SHIPMENTS"
    if (actionNeededItems.length < 1 && pendingItems.length < 1 && processingItems.length < 1 && underReviewItems.length < 1 && resolvedItems.length > 0) {
      servicesListOptions.push(getOptionLabel('SHIPMENTS', 'Shipments', resolvedItems.length, totalItems));
      servicesListOptions.push(...resolvedItems.map((item) => getOptionData(item, 'RESOLVED')));
    } else {
      if (actionNeededItems.length > 0) {
        servicesListOptions.push(getOptionLabel('ACTION_NEEDED', 'Action Needed', actionNeededItems.length, totalItems));
        servicesListOptions.push(...actionNeededItems.map((item) => getOptionData(item, 'ACTION_NEEDED')));
      }

      if (pendingItems.length > 0 || resolvedItems.length > 0) {
        servicesListOptions.push(getOptionLabel('PENDING_RESOLVED', 'Pending/Resolved', pendingItems.length + resolvedItems.length, totalItems));
      }

      if (pendingItems.length > 0) {
        servicesListOptions.push(...pendingItems.map((item) => getOptionData(item, 'PENDING')));
      }

      if (resolvedItems.length > 0) {
        servicesListOptions.push(...resolvedItems.map((item) => getOptionData(item, 'RESOLVED')));
      }

      if (processingItems.length > 0) {
        servicesListOptions.push(getOptionLabel('PROCESSING', 'Processing', processingItems.length, totalItems));
        servicesListOptions.push(...processingItems.map((item) => getOptionData(item, 'PROCESSING')));
      }

      if (underReviewItems.length > 0) {
        servicesListOptions.push(getOptionLabel('UNDER_REVIEW', 'Under Review', underReviewItems.length, totalItems));
        servicesListOptions.push(...underReviewItems.map((item) => getOptionData(item, 'UNDER_REVIEW')));
      }
    }

    return servicesListOptions;
  }, [invoiceSummary, getOptionData, getOptionLabel]);

  return {
    options,
    createShipmentOption,
  };
};
